import { zodResolver } from '@hookform/resolvers/zod'
import { X } from 'lucide-react'
import { KeyboardEvent } from 'react'
import { useForm, useWatch } from 'react-hook-form'

import { SearchInput } from '@/components/filters/search/input'
import { Button } from '@/components/ui/button'
import { Form, FormControl, FormField, FormItem } from '@/components/ui/form'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import useTranslator from '@/hooks/translation'

import { SearchProps, SearchSchema, searchSchema } from './types'

export function SearchFilterForm({
  formClassName,
  searchValue,
  onChange,
  placeholder,
  onClear,
  ...props
}: SearchProps) {
  const form = useForm<SearchSchema>({
    mode: 'onSubmit',
    resolver: zodResolver(searchSchema),
    defaultValues: {
      search: searchValue || '',
    },
  })

  const { control, reset } = form
  const search = useWatch({ control, name: 'search' })
  const { getTranslation } = useTranslator()

  function handleKeyDown(event: KeyboardEvent<HTMLFormElement>) {
    if (event.key === 'Enter') {
      event.preventDefault()
      event.stopPropagation()
    }
  }

  function handleClearSearch() {
    reset({ search: '' })

    if (onChange) {
      const event = {
        target: { value: '' },
      } as React.ChangeEvent<HTMLInputElement>
      onChange(event)
    }

    if (onClear) {
      onClear()
    }
  }

  return (
    <Form {...form}>
      <form className={formClassName} onKeyDown={handleKeyDown}>
        <div className="relative">
          <FormField
            control={control}
            name="search"
            render={({
              field: { onChange: onChangeForm, value, ...field },
            }) => (
              <FormItem>
                <FormControl>
                  <SearchInput
                    placeholder={placeholder}
                    {...field}
                    onChange={(e) => (onChange ? onChange(e) : onChangeForm(e))}
                    value={searchValue || value}
                    {...props}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          {(searchValue || search) && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-3 top-1/2 h-6 w-6 -translate-y-1/2 p-1 hover:bg-muted"
                  onClick={handleClearSearch}
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{getTranslation('common.clearFilter')}</p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </form>
    </Form>
  )
}
